import { useTranslations } from 'gt-react'
import { Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from './theme-provider'

interface ThemeToggleProps {
  className?: string
}

/**
 * Icon button that cycles between light and dark. Animated sun/moon
 * crossfade using absolute positioning — no Framer dependency, so
 * this component is cheap to include in first paint.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme()
  const t = useTranslations('theme')

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={t('toggle_label')}
      className={cn(
        'relative inline-flex h-9 w-9 items-center justify-center rounded-full',
        'border border-border bg-background/60 backdrop-blur',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'transition-colors',
        className,
      )}
    >
      <Sun
        className={cn(
          'h-4 w-4 absolute transition-all',
          theme === 'dark' ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100',
        )}
      />
      <Moon
        className={cn(
          'h-4 w-4 absolute transition-all',
          theme === 'dark' ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0',
        )}
      />
    </button>
  )
}
